/**
 * MobileProducts — Products / Food Items browser.
 *
 * Layout matches the mockup: 4 category tiles at top (Products
 * active) → location line → search bar + Country/Category/Sub-Category
 * dropdowns → list of product cards. Both the thumbnail image and the
 * row are clickable, per the user's annotation.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronRight, Search, ChevronDown } from "lucide-react";
import MobileLocationBar from "./MobileLocationBar";
import { CATEGORY_TILES, CategoryTile } from "./MobileHome";
import { categoryIllustration } from "@/config/categoryIllustrations";

const API = process.env.REACT_APP_BACKEND_URL;
const FP_PURPLE = "#653091";
const FP_GREEN = "#9CCA45";

function DropSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="relative flex items-center rounded-xl bg-white shadow-sm border border-gray-200" data-testid={testId}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-transparent px-3 py-3 text-base outline-none pr-9"
      >
        <option value="">{label}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 w-4 h-4 opacity-60" />
    </label>
  );
}

const PRODUCT_TAGS = ["Perishable", "Leafy Greens", "Fruits", "Vegetables", "Grains", "Dairy", "Meat", "Seafood"];

const PRODUCT_TAG_STYLE = {
  Perishable:   { bg: "#F9C846", color: "#3A2A00" },
  "Leafy Greens": { bg: "#82C049", color: "#0d2b0d" },
  Fruits:       { bg: "#82C049", color: "#0d2b0d" },
  Vegetables:   { bg: "#82C049", color: "#0d2b0d" },
  Dairy:        { bg: "#a8d5f5", color: "#0d2b40" },
  Meat:         { bg: "#c74019", color: "#fff" },
  Seafood:      { bg: "#4C96D2", color: "#fff" },
  Grains:       { bg: "#c39a5c", color: "#3A2A00" },
};

function ProductTag({ label }) {
  const s = PRODUCT_TAG_STYLE[label] || { bg: "#eee", color: "#222" };
  return (
    <span className="inline-block text-[11px] font-bold rounded-md px-2 py-0.5" style={{ background: s.bg, color: s.color }}>
      {label}
    </span>
  );
}

export default function MobileProducts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [sub, setSub] = useState("");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/food-items`, {
      params: {
        q: q || undefined,
        category: category || undefined,
        sub_category: sub || undefined,
        limit: 40,
      },
    })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q, category, sub]);

  return (
    <div data-testid="mobile-products">
      {/* Category tiles with Products active */}
      <div
        className="grid grid-cols-4 gap-2 px-3 pt-3 pb-4"
        style={{ background: "linear-gradient(180deg,#B5DA5E 0%,#CBEB90 100%)" }}
      >
        {CATEGORY_TILES.map((t) => (
          <CategoryTile key={t.key} tile={t} active={t.key === "products"} />
        ))}
      </div>

      {/* Location */}
      <div className="flex items-center justify-center mt-3 mb-3">
        <MobileLocationBar testid="products-location" />
      </div>

      {/* Filters */}
      <div className="px-4 grid grid-cols-2 gap-3">
        <div className="col-span-1 flex items-center gap-2 bg-white rounded-xl shadow-sm border border-gray-200 px-3 py-3">
          <input
            data-testid="products-search-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search Product"
            className="flex-1 outline-none bg-transparent text-base"
          />
          <Search className="w-4 h-4 opacity-60" />
        </div>
        <DropSelect
          label="United States"
          value=""
          onChange={() => {}}
          testId="products-filter-country"
          options={[{ value: "us", label: "United States" }]}
        />
        <DropSelect
          label="Select Category"
          value={category}
          onChange={setCategory}
          testId="products-filter-category"
          options={[
            { value: "produce",   label: "Produce" },
            { value: "dairy",     label: "Dairy" },
            { value: "meat",      label: "Meat & Poultry" },
            { value: "seafood",   label: "Seafood" },
            { value: "grains",    label: "Grains & Bread" },
            { value: "packaged",  label: "Packaged Foods" },
          ]}
        />
        <DropSelect
          label="Select Sub-Category"
          value={sub}
          onChange={setSub}
          testId="products-filter-subcategory"
          options={[
            { value: "leafy-greens",   label: "Leafy Greens" },
            { value: "fruits",         label: "Fruits" },
            { value: "root-vegetables",label: "Root Vegetables" },
            { value: "cheese",         label: "Cheese" },
          ]}
        />
      </div>

      {/* Results */}
      <div className="mt-4 px-4 pb-16 space-y-3" data-testid="products-list">
        {loading ? (
          <div className="text-sm opacity-60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 opacity-60 text-sm">No products match your filters.</div>
        ) : (
          items.map((it) => {
            const cover = it.image_url || (it.images || [])[0] || `https://images.pexels.com/photos/2255935/pexels-photo-2255935.jpeg?auto=compress&cs=tinysrgb&w=400`;
            const tags = it.tags || (it.perishable ? ["Perishable"] : []);
            return (
              <Link
                to={`/m/products/${it.slug}`}
                key={it.slug}
                data-testid={`products-item-${it.slug}`}
                className="flex items-center gap-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-3"
              >
                <img
                  src={cover}
                  alt={it.name}
                  loading="lazy"
                  className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-base font-bold">{it.name}</div>
                  <div className="mt-1 flex gap-1.5 flex-wrap">
                    {tags.slice(0, 3).map((t) => (
                      <ProductTag key={t} label={t} />
                    ))}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 opacity-60" />
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
